// ─── Public Status Page — the verdict ────────────────────────────────
//
// The one thing every visitor came for, answered above the fold in a full
// sentence. "Normal" is a pill label, not an answer; "Alle systemer kører
// normalt" is an answer — so the headline comes from ./i18n/da.ts and the
// server's `overallLabel` is kept as the fallback for a status value this build
// does not recognise (a newer payload shape must still render something true).
//
// role="status" + aria-live="polite" so a screen reader announces a change that
// arrives on the poll loop, without interrupting whatever is being read.

import type { MouseEvent } from 'react';
import type { PublicStatusIncident, StatusComponentStatus } from '@pcp/shared';
import { da } from '../i18n/da';
import { fmtDateTime } from '../lib/format';
import { levelForStatus } from '../lib/levels';
import { isPlainLeftClick } from '../router';
import type { StatusBannerProps } from '../types';
import { SIcon } from '../ui/SIcon';

/** Full-sentence headline per status. Deliberately separate from
 *  STATUS_COMPONENT_STATUS_LABELS_DA, which supplies the short pill words. */
const HEADLINES: Record<StatusComponentStatus, string> = {
  available: da.overallOperational,
  degraded: da.overallDegraded,
  maintenance: da.overallMaintenance,
  unavailable: da.overallOutage,
};

export function StatusBanner({
  overall,
  overallLabel,
  generatedAt,
  activeIncidents,
  onOpenIncident,
  hrefForIncident,
}: StatusBannerProps) {
  const level = levelForStatus(overall);
  // `||`, not `??`: an EMPTY server label must fall through too, not render a
  // blank headline on the one line of the page nobody can afford to miss.
  const headline = HEADLINES[overall] || overallLabel || da.overallUnknown;
  // Everything-is-fine needs no incident list; anything else is explained by the
  // incidents behind it, linked inline so the reader is one click from the detail.
  const showIncidents = overall !== 'available' && activeIncidents.length > 0;

  const handleClick = (incident: PublicStatusIncident) => (event: MouseEvent<HTMLAnchorElement>) => {
    if (!isPlainLeftClick(event)) return;
    event.preventDefault();
    onOpenIncident(incident);
  };

  return (
    <section className={`sp-banner sp-banner--${level.modifier}`} role="status" aria-live="polite">
      <SIcon name={level.icon} size={28} className="sp-banner-icon" />
      <div className="sp-banner-body">
        <p className="sp-banner-title">{headline}</p>
        <p className="sp-banner-meta">{da.lastUpdated(fmtDateTime(generatedAt))}</p>

        {showIncidents && (
          <div className="sp-banner-list">
            <span className="sp-banner-list-title">{da.overallActiveIncidents}</span>
            {activeIncidents.map((incident) => (
              <a
                key={incident.id}
                className="sp-banner-link"
                href={hrefForIncident(incident)}
                onClick={handleClick(incident)}
              >
                <SIcon name="chevronRight" size={14} />
                {incident.title}
              </a>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
